// Browser-compatible Web Vitals monitoring script
// This will be built separately and served to browsers

import { initSentry, SENTRY_DSN } from "./sentry";
import { getMonitoringHost } from "./monitoringHost";
import { onCLS, onINP, onLCP, onFCP, LCPMetricWithAttribution, INPMetricWithAttribution, CLSMetricWithAttribution, FCPMetricWithAttribution } from 'web-vitals/attribution';
import { initUrlTimeline, urlAt } from './url-timeline';
import { getLcpTime, getMetricDateTime, waitForLoad } from './helpers';
import { ConnectionInfo, getConnectionInfo } from './connection';
import MetricsStorage from './metrics-storage';
import { initApi, sendBatchMetrics, sendSessionInfo } from './api';
import { collectNavigationMetrics, collectResourceMetrics } from './observers';
import { captureError, debugLog } from './utils/logger';
import { isLocalHost } from './utils/isLocalHost';
import { getCurrentScriptAttribute } from './utils/currentScript';
import { collectElementData } from './collectElementData';
import { transformCLSMetric } from './transformCLSMetric';
import * as Sentry from "@sentry/browser";

function shouldTriggerLaglogSentryTest(source: 'sdk') {
  const params = new URLSearchParams(window.location.search);
  const queryValue = params.get('laglog_sentry_test') || params.get('sentry_test');
  const globalValue = (window as any).__LAGLOG_SENTRY_TEST__;

  return queryValue === 'all' || queryValue === source || globalValue === 'all' || globalValue === source;
}

async function triggerLaglogSentryTestError(source: 'sdk') {
  const error = new Error(`[Laglog Sentry Test] ${source} forced SDK error`);
  error.name = 'LaglogSentryTestError';

  Sentry.captureException(error, {
    tags: {
      laglog_test: 'sentry',
      source,
    },
  });

  try {
    await Sentry.flush(2000);
  } finally {
    setTimeout(() => {
      throw error;
    }, 0);
  }
}

// Initialise Sentry FIRST (installs global error handlers before any other work),
// then configure the metrics API host from build-time env or the stable default.
// Keep these the first executable statements. See docs/KNOWN_ISSUES.md.
initSentry();
initApi(getMonitoringHost(import.meta.env.LAGLOG_MONITORING_HOST));

if (SENTRY_DSN && shouldTriggerLaglogSentryTest('sdk')) {
  void triggerLaglogSentryTestError('sdk');
}

let sessionId: string | null = null;

// Generate unique session ID
function generateSessionId(): string {
  return 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
}

// Get or create session ID
function getSessionId(): string {
  if (!sessionId) {
    sessionId = generateSessionId();
    debugLog('Generated new Web Vitals session ID:', sessionId);
  }
  return sessionId;
}


function collectWebVitals(siteId: string) {
  const version = (window as any).__APP_VERSION__ || 'unknown';
  // Version of the monitored page's own app (above) vs. version of this SDK
  // bundle (below) — two different things, reported as two different fields.
  // Inlined at build time and sent as-is: validating it here would only add
  // bytes to a bundle that runs on every page view, and the server validates
  // the payload anyway.
  const sdkVersion: string | undefined = import.meta.env.LAGLOG_SDK_VERSION;
  const sessionId = getSessionId();

  debugLog(`collectWebVitals called with siteId: ${siteId}, sessionId: ${sessionId}, version: ${version}, sdkVersion: ${sdkVersion}`);

  const storage = new MetricsStorage(10000, async (metrics) => {
    sendBatchMetrics(metrics, siteId, sessionId);
  });

  initUrlTimeline(segment => storage.addMetric('history', segment));

  sendSessionInfo(siteId, sessionId, version, sdkVersion, getConnectionInfo());

  const handleLCPMetric = async (metric: LCPMetricWithAttribution) => {
    debugLog("🚀 ~ handleLCPMetric ~ metric:", metric);

    const data = {
      siteId,
      sessionId,
      pageUrl: urlAt(getLcpTime(metric)),
      connection: getConnectionInfo(),
      version,
      delta: metric.delta,
      value: metric.value,
      id: metric.id,
      attribution: {
        elementRenderDelay: metric.attribution.elementRenderDelay,
        resourceLoadDelay: metric.attribution.resourceLoadDelay,
        resourceLoadDuration: metric.attribution.resourceLoadDuration,
        target: metric.attribution.target,
        url: metric.attribution.url,
        lcpEntry: {
          duration: metric.attribution.lcpEntry?.duration,
          name: metric.attribution.lcpEntry?.name,
          startTime: metric.attribution.lcpEntry?.startTime,
          renderTime: metric.attribution.lcpEntry?.renderTime,
          loadTime: metric.attribution.lcpEntry?.loadTime,
          size: metric.attribution.lcpEntry?.size,
          url: metric.attribution.lcpEntry?.url,
          element: metric.attribution.lcpEntry?.element ? collectElementData(metric.attribution.lcpEntry.element) : null,
        }
      }
    };
    debugLog("🚀 ~ handleLCPMetric ~ data:", data)

    storage.addMetric('LCP', data);
  }

  const handleINPMetric = (metric: INPMetricWithAttribution) => {
    debugLog("🚀 ~ handleINPMetric ~ metric:", metric);

    const serializeScriptTiming = (script: PerformanceScriptTiming) => {
      if (!script) return undefined;
      return {
        startTime: script.startTime,
        duration: script.duration,
        name: script.name,
        entryType: script.entryType,
        executionStart: script.executionStart,
        forcedStyleAndLayoutDuration: script.forcedStyleAndLayoutDuration,
        invoker: script.invoker,
        invokerType: script.invokerType,
        pauseDuration: script.pauseDuration,
        sourceUrl: script.sourceURL,
        sourceFunctionName: script.sourceFunctionName,
        sourceCharPosition: script.sourceCharPosition,
        windowAttribution: script.windowAttribution,
      };
    };

    // Identify longestScript.entry for marking the matching LAF script
    const longestEntry = (metric.attribution.longestScript as any)?.entry;
    const longestScriptSubpart = metric.attribution.longestScript?.subpart;
    const longestScriptIntersectingDuration = metric.attribution.longestScript?.intersectingDuration;

    const isLongestScriptMatch = (script: any, longestEntry: any) => {
      if (!longestEntry) return false;
      return script.startTime === longestEntry.startTime
        && script.duration === longestEntry.duration
        && script.name === longestEntry.name
        && script.entryType === longestEntry.entryType
        && script.executionStart === longestEntry.executionStart
        && script.forcedStyleAndLayoutDuration === longestEntry.forcedStyleAndLayoutDuration
        && script.invoker === longestEntry.invoker
        && script.invokerType === longestEntry.invokerType
        && script.pauseDuration === longestEntry.pauseDuration
        && script.sourceURL === longestEntry.sourceURL
        && script.sourceFunctionName === longestEntry.sourceFunctionName
        && script.sourceCharPosition === longestEntry.sourceCharPosition
        && script.windowAttribution === longestEntry.windowAttribution;
    };

    const data = {
      siteId,
      sessionId,
      connection: getConnectionInfo(),
      navigationType: metric.navigationType,
      delta: metric.delta,
      value: metric.value,
      id: metric.id,
      attribution: {
        inputDelay: metric.attribution.inputDelay,
        interactionTarget: metric.attribution.interactionTarget,
        interactionTime: metric.attribution.interactionTime,
        interactionType: metric.attribution.interactionType,
        loadState: metric.attribution.loadState,
        longAnimationFrameEntries: (metric.attribution.longAnimationFrameEntries || []).map(entry => ({
          startTime: entry.startTime,
          duration: entry.duration,
          name: entry.name,
          entryType: entry.entryType,
          blockingDuration: entry.blockingDuration,
          firstUIEventTimestamp: entry.firstUIEventTimestamp,
          renderStart: entry.renderStart,
          styleAndLayoutStart: entry.styleAndLayoutStart,
          scripts: (entry.scripts || []).map(script => {
            const serialized = serializeScriptTiming(script);
            if (!serialized) return null;
            
            const isLongestScript = isLongestScriptMatch(script, longestEntry);
            
            return {
              ...serialized,
              longestScript: isLongestScript,
              longestScriptSubpart: isLongestScript ? longestScriptSubpart ?? null : null,
              longestScriptIntersectingDuration: isLongestScript ? longestScriptIntersectingDuration ?? null : null,
            };
          }).filter((value): value is NonNullable<typeof value> => Boolean(value)),
        })),
        nextPaintTime: metric.attribution.nextPaintTime,
        presentationDelay: metric.attribution.presentationDelay,
        processingDuration: metric.attribution.processingDuration,
        totalPaintDuration: metric.attribution.totalPaintDuration,
        totalScriptDuration: metric.attribution.totalScriptDuration,
        totalStyleAndLayoutDuration: metric.attribution.totalStyleAndLayoutDuration,
        totalUnattributedDuration: metric.attribution.totalUnattributedDuration,
        processedEventEntries: metric.attribution.processedEventEntries.map(entry => ({
          startTime: entry.startTime,
          duration: entry.duration,
          entryType: entry.entryType,
          interactionId: entry.interactionId,
          name: entry.name,
          processingEnd: entry.processingEnd,
          processingStart: entry.processingStart,
          target: entry.target ? collectElementData(entry.target as Element) : null,
          browserEntry: metric.entries.some(e =>
            e.startTime === entry.startTime &&
            e.duration === entry.duration &&
            e.name === entry.name &&
            e.entryType === entry.entryType &&
            e.interactionId === entry.interactionId &&
            e.processingStart === entry.processingStart &&
            e.processingEnd === entry.processingEnd
          ),
        }))
      },
    };
    debugLog("🚀 ~ handleINPMetric ~ data:", data)
    storage.addMetric('INP', data);
  }

  const handleCLSMetric = (metric: CLSMetricWithAttribution) => {
    const data = transformCLSMetric(metric, siteId, sessionId, version);
    storage.addMetric('LS', data);
  }

  const handleFCPMetric = (metric: FCPMetricWithAttribution) => {
    debugLog("🚀 ~ handleFCPMetric ~ metric:", metric);
    const data = {
      sessionId,
      siteId,
      connection: getConnectionInfo(),
      version,
      delta: metric.delta,
      value: metric.value,
      id: metric.id,
      attribution: {
        firstByteToFCP: metric.attribution?.firstByteToFCP,
        loadState: metric.attribution?.loadState,
      }
    }
    debugLog("🚀 ~ handleFCPMetric ~ data:", data)

    storage.addMetric('FCP', data);
  }

  // Collect Core Web Vitals with attribution
  onLCP(handleLCPMetric, { reportAllChanges: true });
  onINP(handleINPMetric, { reportAllChanges: true });
  onCLS(handleCLSMetric, { reportAllChanges: true });
  onFCP(handleFCPMetric, { reportAllChanges: true });

  // Collect detailed resource metrics
  collectResourceMetrics((entry => { storage.addMetric('resource', entry); }));
  collectNavigationMetrics((entry => { storage.addMetric('navigation', entry); }));
}

// A test override is the one way to force a siteId (used by SDK e2e/tests); it
// bypasses the localhost guard below so tests can run against a local server.
const testSiteId = (window as any).__WEBVITALS_TEST_SITE_ID__;
// The registered siteId is passed on the SDK's own <script> tag as
// `data-site-id` (see the dashboard's integration snippet). Fall back to a
// preset global, then to the page hostname for backwards compatibility.
const configuredSiteId = getCurrentScriptAttribute('data-site-id') || (window as any).__WEBVITALS_SITE_ID__;
const siteId = testSiteId || configuredSiteId || window.location.hostname;

// Never send metrics from localhost: siteId is generated from the registered
// domain, and the local hostname is not a trackable site. Skip collection
// entirely on localhost unless an explicit test override is set.
if (siteId && (testSiteId || !isLocalHost(window.location.hostname))) {
  try {
    collectWebVitals(siteId);
  } catch (error) {
    captureError(error, "Error collecting web vitals");
  }

}
